<template>
  <div class="d-flex align-items-stretch dashboard">
    <!-- Sidebar -->
    <button
      :class="{ 'is-active': isShowSidebar }"
      class="btn-toggler"
      @click="handleClickToggler"
    >
      <div
        :class="{ 'is-active': isShowSidebar }"
        class="hamburger hamburger--squeeze js-hamburger"
      >
        <div class="hamburger-box">
          <div class="hamburger-inner"></div>
        </div>
      </div>
    </button>
    <div id="sidebar-wrapper" :class="sidebarClassList">
      <side-bar :key="sidebarKey"></side-bar>
    </div>
    <!-- Page Content -->
    <div id="page-content-wrapper" class="col p-0 d-flex">
      <router-view></router-view>
    </div>
  </div>
</template>

<script>

import SideBar from "@/components/admin/layout/SideNavBar";
import { FCM_CONFIG, STORAGE_KEYS, buildFcmTopic } from "@/config/fcm";
import { getMessagingIfSupported } from "@/setup/firebaseMessaging";
import { getCookie, Auth } from "@/lib/cookie";
import axios from "axios";
import { getToken, onMessage } from "firebase/messaging";

export default {
  name: "AdminDashboard",
  components: {
    SideBar
  },
  data() {
    return {
      isShowSidebar: false,
      messaging: null,
      sidebarKey: 0
    };
  },
  computed: {
    sideBar() {
      return this.$store.state.sideBar;
    },
    sidebarClassList() {
      return { hide: !this.isShowSidebar };
    }
  },
  mounted() {
    this.initFcm();

    // The cached entitlement is only ever written at login, so until now a role
    // change stayed invisible for as long as the admin kept their session. It
    // is refreshed on every load instead, to keep the menu in step with what
    // the API will actually authorize.
    this.getAdminAuth();
  },
  methods: {
    handleClickToggler() {
      this.isShowSidebar = !this.isShowSidebar;
    },
    reloadSidebar() {
      this.sidebarKey += 1;
    },
    async initFcm() {
      if (!FCM_CONFIG.VAPID_KEY || !FCM_CONFIG.SERVER_KEY) return;

      this.messaging = await getMessagingIfSupported();
      if (!this.messaging) return;

      // Attach listeners up front so they stay active even if the
      // permission request or web-push setup below fails.
      this.registerMessageListeners();

      try {
        await this.requestNotificationPermission();
        await this.initWebPush();
      } catch (error) {
        // Swallow setup failures: a failed token registration must not
        // tear down the already-registered message listeners.
      }
    },
    async requestNotificationPermission() {
      const permission = await Notification.requestPermission();

      if (permission !== "granted") {
        throw new Error("Notification permission not granted");
      }
    },
    registerMessageListeners() {
      if (!this.messaging) return;

      this.$store.commit("resetNotification");

      onMessage(this.messaging, payload => {
        if (this.validateNotificationPayload(payload)) {
          this.$store.commit("addNotification", payload);
          this.pushNotification(payload);
        }
      });

      // NOTE: firebase 9+ removed onTokenRefresh(); getToken() inside
      // initWebPush already returns a fresh token on every app start.
    },
    async initWebPush() {
      const registration = await navigator.serviceWorker.register(
        `${window.location.origin}/static/firebase-messaging-sw.js`
      );
      const fcmToken = await this.getFcmToken(FCM_CONFIG.VAPID_KEY, registration);
      await this.registerToken(fcmToken);
    },
    async getFcmToken(vapidKey, serviceWorkerRegistration) {
      return getToken(this.messaging, { vapidKey, serviceWorkerRegistration });
    },
    async registerToken(token) {
      const permission = JSON.parse(
        localStorage.getItem(STORAGE_KEYS.ADMIN_ROLE_PERMISSION)
      );

      const roleId = permission ? permission.roleId : null;
      if (!roleId) {
        throw new Error("roleId not found for this admin");
      }

      const topic = buildFcmTopic(roleId);
      await this.subscribeFcm(token, topic);
    },
    async subscribeFcm(token, topic) {
      // TODO(security): move this topic subscription to the backend. Posting to
      // iid.googleapis.com from the browser exposes FCM_CONFIG.SERVER_KEY to
      // every client. Frontend should send `token` to our API and let the
      // server subscribe. See src/config/fcm.js SERVER_KEY note.
      await axios.post(
        `https://iid.googleapis.com/iid/v1/${token}/rel/topics/${topic}`,
        {},
        {
          headers: {
            Authorization: FCM_CONFIG.SERVER_KEY
          }
        }
      );
    },
    validateNotificationPayload(payload) {
      return !!(
        payload?.data &&
        payload?.notification?.title &&
        payload?.notification?.body
      );
    },
    pushNotification(payload) {
      // Skip if this notification is for another admin
      if (payload.data.adminId) {
        return;
      }

      const noti = new Notification(payload.notification.title, {
        body: payload.notification.body,
        icon: "/static/icons/logo-brj.png"
      });

      if (payload.data.targetFeature === "CONVERSATION") {
        noti.onclick = () => this.handleConversationNotification(payload);
      }
    },
    handleConversationNotification(payload) {
      const projectId = payload.data.projectId || "OTHER";
      const projectName = payload.data.projectName || "Other";

      // Update Vuex store
      this.$store.commit("setProjectId", projectId);
      this.$store.commit("setProjectName", projectName);

      // Persist to localStorage
      localStorage.setItem(STORAGE_KEYS.PROJECT_ID, projectId);
      localStorage.setItem(STORAGE_KEYS.PROJECT_NAME, projectName);

      // Navigate to contact
      this.$router.push({
        name: "Contact",
        params: { contactId: payload.data.targetId }
      });
    },
    getAdminAuth() {
      const cachedPermission = localStorage.getItem(
        STORAGE_KEYS.ADMIN_ROLE_PERMISSION
      );

      const request = axios.get(`${process.env.HOST}/api/admin/auth/`, {
        headers: {
          Authorization: `Bearer ${getCookie(Auth.AccessToken)}`
        }
      });

      if (!request || typeof request.then !== "function") {
        return Promise.resolve();
      }

      return request
        .then(({ data }) => {
          const permission = JSON.stringify(data.adminRole);

          localStorage.setItem("adminFirstName", data.firstName);
          localStorage.setItem("adminLastName", data.lastName);
          localStorage.setItem("adminRole", data.role);
          localStorage.setItem(STORAGE_KEYS.ADMIN_ROLE_PERMISSION, permission);
          localStorage.setItem("adminRoleId", data.adminRole.roleId);
          localStorage.setItem("adminId", data.adminId);
          localStorage.setItem(
            "adminProjects",
            data.adminProjects.map(project => project.projectId).join()
          );

          return permission !== cachedPermission;
        })
        .then(hasPermissionChanged => {
          // Remounting the sidebar is what makes it re-read the cache, so it is
          // only worth doing when the entitlement actually moved.
          if (hasPermissionChanged) {
            this.reloadSidebar();
          }
        })
        .catch(() => {
          // A refresh that fails leaves the previous entitlement cached. The
          // sidebar keeps rendering from it rather than emptying out, which is
          // the same position the session was already in.
        });
    }
  }
};
</script>

<style>
body {
  overflow-x: hidden;
}

#sidebar-wrapper {
  flex: 0 0 240px;
  background: #fff;
  -webkit-transition: all 0.5s ease;
  -moz-transition: all 0.5s ease;
  -o-transition: all 0.5s ease;
  transition: all 0.5s ease;
  text-align: left;
  border-right: solid 1px #e0e0e0;
  overflow: auto;
}

.toggled#sidebar-wrapper {
  width: 50px;
}

#page-content-wrapper {
  min-width       : 0;
  max-width       : calc(100% - 240px);
  padding-left    : 0;
  background-color: #f9f9f9;
  text-align      : left;
}

.title {
  font-size: 25px;
  font-weight: bold;
  color: #000;
}
.content-title {
  height: 75px;
}
/* add Montserrat later */
.content-title {
  padding: 0px 30px;
  padding-top: 20px;
  height: 110px;
  font-size: 32px;
  font-weight: bold;
  line-height: 0.68;
  letter-spacing: -0.2px;
  text-align: left;
  color: #000000;
  flex-shrink: 0;
}
.content-title h1 {
  font-size: 24px;
  font-weight: bold;
  font-style: normal;
  font-stretch: normal;
  line-height: 1.29;
  letter-spacing: -0.1px;
  color: #000000;
}
.content-title .sub-title {
  font-size: 16px;
  font-weight: 500;
  font-style: normal;
  font-stretch: normal;
  line-height: 1.63;
  letter-spacing: normal;
  color: #8c8786;
}
.content-title a {
  color: #8c8786;
}
.content-head {
  font-size: 40px;
  font-weight: bold;
  line-height: 0.68;
  letter-spacing: -0.2px;
  text-align: left;
  color: #000000;
}
.content-sub-head {
  font-size: 14px;
  font-weight: 500;
  text-align: left;
  color: #9b9b9b;
}

.tab {
  font-size: 18px;
  font-weight: bold;
  line-height: 1.17;
  letter-spacing: -0.1px;
  text-align: center;
  color: #b9b9b9;
  cursor: pointer;
}
.tab.active {
  color: #000;
  border-bottom: 2px solid #ef7e18;
}

.btn-toggler {
  display: none;
  padding: 0;
  border: 1px solid #e0e0e0;
  transition: all 0.5s ease;
}

.btn-toggler:focus {
  outline: 0;
}

.hamburger {
  font: inherit;
  display: inline-block;
  overflow: visible;
  margin: 0;
  padding: 15px;
  cursor: pointer;
  text-transform: none;
  color: inherit;
  border: 0;
  background-color: transparent;
}

.hamburger-box {
  position: relative;
  display: inline-block;
  width: 30px;
  height: 20px;
}

.hamburger-inner,
.hamburger-inner:after,
.hamburger-inner:before {
  content: "";

  position: absolute;
  left: 0;
  width: 30px;
  height: 3px;
  border-radius: 4px;
  background-color: #ef7e18;
}

.hamburger-inner:before {
  top: -10px;
}

.hamburger-inner:after {
  top: 10px;
}

.hamburger.is-active .hamburger-inner,
.hamburger.is-active .hamburger-inner:after,
.hamburger.is-active .hamburger-inner:before {
  background-color: #ef7e18;
}

.hamburger-inner {
  top: 50%;
  display: block;
}

.hamburger.is-active:hover,
.hamburger:hover {
  opacity: 0.7;
}

.hamburger--squeeze.is-active {
  transform: rotate(45deg);
}

.hamburger--squeeze.is-active .hamburger-inner:before {
  top: 0;
  opacity: 0;
}

.hamburger--squeeze.is-active .hamburger-inner:after {
  top: 0;
  transform: rotate(-90deg);
}

@media (max-width: 768px) {
  .btn-toggler {
    display: block;
    position: fixed;
    left: 0;
    border-radius: 0;
    background: white;
    z-index: 9999;
  }

  .btn-toggler.is-active {
    left: 240px;
  }

  #sidebar-wrapper {
    width: 240px;
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    z-index: 9998;
  }

  #sidebar-wrapper.hide {
    transform: translateX(-240px);
  }

  #page-content-wrapper {
    max-width: none;
    width: 100%;
    flex: auto;
  }
}
</style>
